import { httpClient } from './http-client';
import type { ApiResponse } from '../types/api';

/**
 * Categories Service
 * Handles category-related API calls
 */

export interface CategoryGroup {
  id: string;
  name: string;
  type: 'income' | 'expense' | 'transfer';
  icon?: string | null;
  color?: string | null;
  sort_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  group_id: string;
  name: string;
  icon?: string | null;
  sort_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryWithGroup extends Category {
  group_name: string;
  group_type: 'income' | 'expense' | 'transfer';
  group_icon?: string | null;
  group_color?: string | null;
}

export interface CreateCategoryGroupRequest {
  name: string;
  type: 'income' | 'expense' | 'transfer';
  icon?: string;
  color?: string;
  sort_order?: number;
}

interface UpdateCategoryGroupRequest {
  name?: string;
  type?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
}

export interface CreateCategoryRequest {
  group_id: string;
  name: string;
  icon?: string;
  sort_order?: number;
}

interface UpdateCategoryRequest {
  name?: string;
  group_id?: string;
  icon?: string;
  sort_order?: number;
}

export class CategoriesService {
  private static readonly GROUPS_URL = '/category-groups';
  private static readonly CATEGORIES_URL = '/categories';

  // Category Groups

  static async getGroups(): Promise<ApiResponse<{ groups: CategoryGroup[]; total: number }>> {
    const raw = await httpClient.get<any>(CategoriesService.GROUPS_URL);
    const data = (raw as any).data || raw;
    return {
      data: {
        groups: data.groups || [],
        total: data.total || 0,
      },
    };
  }

  static async createGroup(req: CreateCategoryGroupRequest): Promise<ApiResponse<CategoryGroup>> {
    const result = await httpClient.post<any>(CategoriesService.GROUPS_URL, req);
    return { data: (result as any).data || result };
  }

  static async updateGroup(id: string, req: UpdateCategoryGroupRequest): Promise<ApiResponse<CategoryGroup>> {
    const result = await httpClient.put<any>(`${CategoriesService.GROUPS_URL}/${id}`, req);
    return { data: (result as any).data || result };
  }

  static async deleteGroup(id: string): Promise<void> {
    await httpClient.delete(`${CategoriesService.GROUPS_URL}/${id}`);
  }

  // Categories

  static async getCategories(): Promise<ApiResponse<{ categories: CategoryWithGroup[]; total: number }>> {
    const raw = await httpClient.get<any>(CategoriesService.CATEGORIES_URL);
    const data = (raw as any).data || raw;
    return {
      data: {
        categories: data.categories || [],
        total: data.total || 0,
      },
    };
  }

  static async createCategory(req: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    const result = await httpClient.post<any>(CategoriesService.CATEGORIES_URL, req);
    return { data: (result as any).data || result };
  }

  static async updateCategory(id: string, req: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    const result = await httpClient.put<any>(`${CategoriesService.CATEGORIES_URL}/${id}`, req);
    return { data: (result as any).data || result };
  }

  static async deleteCategory(id: string): Promise<void> {
    await httpClient.delete(`${CategoriesService.CATEGORIES_URL}/${id}`);
  }
}
